import axios from 'axios';

// 创建axios实例
const api = axios.create({
  baseURL: '/prod-api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 从localStorage获取token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // 从localStorage获取username
    const user = localStorage.getItem('user');
    if (user) {
      const userData = JSON.parse(user);
      if (userData.username) {
        config.headers['Username'] = userData.username;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    // 直接返回response，让业务逻辑处理数据结构
    return response;
  },
  (error) => {
    console.error('API请求错误:', error);

    // 如果是401未认证错误，清除认证信息
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // 可以在这里添加重定向到登录页面的逻辑
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    // 尝试从错误响应中提取错误信息
    const errorMessage = error.response?.data?.message ||
                         error.response?.data?.error ||
                         error.message ||
                         '请求失败';
    return Promise.reject(new Error(errorMessage));
  }
);

// 订单管理API
export const orderApi = {
  // 分页查询订单
  queryOrders: (params) => {
    return api.post('/api/order-management/query', params);
  },

  // 获取订单详情
  getOrderDetail: (orderId, username) => {
    const user = localStorage.getItem('user');
    const userData = user ? JSON.parse(user) : {};
    const currentUsername = username || userData.username;
    return api.get(`/api/order-management/${orderId}?username=${currentUsername}`);
  },

  // 获取订单状态列表
  getStatusList: () => {
    return api.get('/api/order-management/status-list');
  },

  // 快速搜索订单
  quickSearch: (params, username) => {
    const user = localStorage.getItem('user');
    const userData = user ? JSON.parse(user) : {};
    const currentUsername = username || userData.username;
    const searchParams = {
      ...params,
      username: currentUsername
    };
    return api.get('/api/order-management/search', { params: searchParams });
  },

  // 导出订单数据到Excel
  exportOrders: (exportParams) => {
    const user = localStorage.getItem('user');
    const userData = user ? JSON.parse(user) : {};
    const currentUsername = exportParams.username || userData.username;

    const params = {
      ...exportParams,
      username: currentUsername
    };

    return api.post('/api/order-management/export', params, {
      responseType: 'blob', // 重要：设置响应类型为blob以处理文件下载
    });
  },
};

// Excel上传API
export const excelApi = {
  // 解析Excel文件
  parseExcel: (formData, username) => {
    const user = localStorage.getItem('user');
    const userData = user ? JSON.parse(user) : {};
    const currentUsername = username || userData.username;
    return api.post('/system/excel/parse', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Username': currentUsername
      },
    });
  },
};

// 订单统计API
export const statisticsApi = {
  // 获取订单平台状态统计
  getOrderStatusStatistics: (username) => {
    const user = localStorage.getItem('user');
    const userData = user ? JSON.parse(user) : {};
    const currentUsername = username || userData.username;
    return api.get('/api/order-statistics/order-status', { params: { username: currentUsername } });
  },

  // 获取Amazon物流状态统计
  getAmazonLogisticsStatusStatistics: (username) => {
    const user = localStorage.getItem('user');
    const userData = user ? JSON.parse(user) : {};
    const currentUsername = username || userData.username;
    return api.get('/api/order-statistics/amazon-logistics-status', { params: { username: currentUsername } });
  },

  // 获取订单状态综合统计
  getAllOrderStatistics: (username) => {
    const user = localStorage.getItem('user');
    const userData = user ? JSON.parse(user) : {};
    const currentUsername = username || userData.username;
    return api.get('/api/order-statistics/all', { params: { username: currentUsername } });
  },
};

// 账户积分API
export const accountPointsApi = {
  // 获取账户积分
  getAccountPoints: (username) => {
    const user = localStorage.getItem('user');
    const userData = user ? JSON.parse(user) : {};
    const currentUsername = username || userData.username;
    return api.get('/api/account-points', { params: { username: currentUsername } });
  },

  // 获取积分使用明细分页列表
  getPointUsageDetails: (params) => {
    const user = localStorage.getItem('user');
    const userData = user ? JSON.parse(user) : {};
    const currentUsername = params.username || userData.username;
    const searchParams = {
      ...params,
      username: currentUsername
    };
    return api.get('/api/account-points/usage-details', { params: searchParams });
  },

  // 获取积分充值明细分页列表
  getPointRechargeDetails: (params) => {
    const user = localStorage.getItem('user');
    const userData = user ? JSON.parse(user) : {};
    const currentUsername = params.username || userData.username;
    const searchParams = {
      ...params,
      username: currentUsername
    };
    return api.get('/api/account-points/recharge-details', { params: searchParams });
  },
};

// 公告API
export const announcementApi = {
  // 获取用户公告列表
  getUserAnnouncements: (username, limit = 5) => {
    const user = localStorage.getItem('user');
    const userData = user ? JSON.parse(user) : {};
    const currentUsername = username || userData.username;
    return api.get('/api/announcements', { params: { username: currentUsername, limit } });
  },

  // 获取所有公告列表（管理员用）
  getAllAnnouncements: (username) => {
    const user = localStorage.getItem('user');
    const userData = user ? JSON.parse(user) : {};
    const currentUsername = username || userData.username;
    return api.get('/api/announcements/admin', { params: { username: currentUsername } });
  },

  // 创建公告
  createAnnouncement: (username, data) => {
    const user = localStorage.getItem('user');
    const userData = user ? JSON.parse(user) : {};
    const currentUsername = username || userData.username;
    return api.post('/api/announcements', data, { params: { username: currentUsername } });
  },

  // 编辑公告
  updateAnnouncement: (username, data) => {
    const user = localStorage.getItem('user');
    const userData = user ? JSON.parse(user) : {};
    const currentUsername = username || userData.username;
    return api.put('/api/announcements', data, { params: { username: currentUsername } });
  },

  // 失效公告
  disableAnnouncement: (username, announcementId) => {
    const user = localStorage.getItem('user');
    const userData = user ? JSON.parse(user) : {};
    const currentUsername = username || userData.username;
    return api.put(`/api/announcements/${announcementId}/disable`, {}, { params: { username: currentUsername } });
  },

  // 启用公告
  enableAnnouncement: (username, announcementId) => {
    const user = localStorage.getItem('user');
    const userData = user ? JSON.parse(user) : {};
    const currentUsername = username || userData.username;
    return api.put(`/api/announcements/${announcementId}/enable`, {}, { params: { username: currentUsername } });
  },
};

// 订单状态更新API
export const orderStatusUpdateApi = {
  // 批量更新订单状态为待拍单
  batchUpdateToPlace: (username, orderIds) => {
    const user = localStorage.getItem('user');
    const userData = user ? JSON.parse(user) : {};
    const currentUsername = username || userData.username;

    return api.post('/api/order/batch-update-to-place',
      { orderIds },
      {
        params: { username: currentUsername },
        headers: {
          'Username': currentUsername
        }
      }
    );
  },

  // 批量删除订单
  batchDeleteOrders: (username, orderIds) => {
    const user = localStorage.getItem('user');
    const userData = user ? JSON.parse(user) : {};
    const currentUsername = username || userData.username;

    return api.post('/api/order/batch-delete',
      { orderIds },
      {
        params: { username: currentUsername },
        headers: {
          'Username': currentUsername
        }
      }
    );
  },

  // 批量重置订单状态为待审核
  batchResetToPendingReview: (username, orderIds) => {
    const user = localStorage.getItem('user');
    const userData = user ? JSON.parse(user) : {};
    const currentUsername = username || userData.username;

    return api.post('/api/order/batch-reset-to-pending-review',
      { orderIds },
      {
        params: { username: currentUsername },
        headers: {
          'Username': currentUsername
        }
      }
    );
  },
};

export default api;